import { ref, computed } from 'vue';

/**
 * Activity (v3 homepage) — a recent event stream, from `POST Report/activity`:
 * who did what, to which document, when. `self` pattern; caller injects an
 * axios-like `http`. The endpoint returns raw `document_activity` rows; this
 * composable maps each event TYPE to an icon + tone and a relative timestamp,
 * keeping presentation thin. Navigation stays in the presentation layer.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number} [options.getLimit] rows before "Show all" (default 8)
 * @param {() => number} [options.getDays] recent window in days (default 30)
 * @param {() => ('all'|'mine')} [options.getScope]
 * @param {() => void} [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 50;

// Keyword → { icon, tone }. Event-type/description vocabulary is client/flow
// dependent, so match by substring with a sensible default. Order matters
// (first hit wins) — more specific phrases first.
const TYPE_RULES = [
  [/sent back|return|revis|reject/i, { icon: 'undo', tone: 'amber' }],
  [/approv|sign.?off|complete/i, { icon: 'check_circle', tone: 'green' }],
  [/submit|sent for|request/i, { icon: 'send', tone: 'accent' }],
  [/comment|note|feedback/i, { icon: 'chat_bubble', tone: 'violet' }],
  [/assign|deleg|invite/i, { icon: 'person_add', tone: 'accent' }],
  [/level|evaluat|scor/i, { icon: 'straighten', tone: 'accent' }],
  [/creat|add|new/i, { icon: 'add_circle', tone: 'green' }],
  [/status|state|moved|transition/i, { icon: 'swap_horiz', tone: 'neutral' }],
  [/edit|updat|chang/i, { icon: 'edit', tone: 'neutral' }],
];
const DEFAULT_RULE = { icon: 'history', tone: 'neutral' };

const relTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < 0) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (diff < 3600000) return diff < 120000 ? 'Just now' : `${Math.floor(diff / 60000)}m ago`;
  if (diff < day) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export function activity(options) {
  const { http, getLimit = () => 8, getDays = () => 30, getScope = () => 'all', onReady = () => {} } = options || {};
  const limit = () => getLimit() || 8;

  const self = { rows: ref([]), loading: ref(false), showAll: ref(false) };

  self.isLoading = computed(() => self.loading.value);
  self.count = computed(() => self.rows.value.length);
  self.hasMore = computed(() => self.count.value > limit());
  self.shownRows = computed(() => (self.showAll.value ? self.rows.value : self.rows.value.slice(0, limit())));

  // --- per-row presentation ---
  const ruleFor = (r) => {
    const hay = `${(r && r.activity_type) || ''} ${(r && r.activity_description) || ''}`;
    for (const [re, rule] of TYPE_RULES) if (re.test(hay)) return rule;
    return DEFAULT_RULE;
  };
  self.icon = (r) => ruleFor(r).icon;
  // Tone: a few event types read as positive (approved/published) or warning
  // (boundary/sent back); everything else is neutral — matches V3Activity's
  // is-good / is-warn row treatment.
  self.tone = (r) => {
    const hay = `${(r && r.activity_type) || ''} ${(r && r.activity_description) || ''}`;
    if (/approv|publish|sign.?off|complete/i.test(hay)) return 'good';
    if (/sent back|return|revis|reject|boundary|flag/i.test(hay)) return 'warn';
    return '';
  };
  self.docName = (r) => (r && (r.doc_title || r.doc_name || r.reference)) || '';
  self.who = (r) => (r && r.activity_by ? String(r.activity_by).trim() : 'Someone');
  // The action verb — the short `activity_type` ("Submitted", "Approved"…),
  // lowercased; falls back to the longer description.
  self.action = (r) => String((r && (r.activity_type || r.activity_description)) || 'updated').toLowerCase();
  // The target the action was performed on (the document).
  self.target = (r) => self.docName(r);
  self.whenText = (r) => relTime(r && r.activity_date);
  // Back-compat single-line form: "Jane Doe · submitted".
  self.line = (r) => [self.who(r), self.action(r)].filter(Boolean).join(' · ');

  async function load() {
    self.loading.value = true;
    try {
      const resp = await http.post('Report/activity', { limit: SERVER_LIMIT, days: getDays() || 30, scope: getScope() || 'all' });
      const data = resp && resp.data;
      self.rows.value = Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
